import { stateRef } from '../../framework/store'
import { RERENDER_EVENT, setValuesInternal } from '../../framework/actions'
import { SCM_ONE_NAV_APP_NAME } from '../../framework/constants'
import { generateNavActionExposes, processActions } from './routes'
import { useGlobalEventListener } from './appEffects'
import { clearLoadingMask } from '../../framework/loadingMask'
import { isEmptyObject } from '../../framework/utils'
import { LeftNavLoadingIcon, loadingLeftNavItems } from '../mainOneappNew/navUtils'
import { __info } from '../../init/debug'
import type { InitedMicroAppConfig, NavItem } from '../../framework/microAppTypes'
import type { FC } from 'react'

export const forceRender = (initedMicroApps = stateRef.current.initedMicroApps) => {
  if (!initedMicroApps?.length) {
    console.error('micro-apps are empty or not provided for forceRerender')
    return // ignore if not provide micro-apps
  }
  const values = generateNavActionExposes(initedMicroApps)
  if (initedMicroApps !== stateRef.current.initedMicroApps) {
    values.initedMicroApps = initedMicroApps
  }
  __info(`finalized micro-apps for tsg: ${initedMicroApps.map(mfe => mfe.id || mfe.appName).join(',')}`)
  __info('merged left navigation and actions', values.leftMainNavigation, values.leftMainActions)
  setValuesInternal(values)
}

export const prerenderLeftNav = (microApps: InitedMicroAppConfig[] & { cached: Record<string, NavItem[]>, icons: Record<string, FC> }) => {
  if (stateRef.current.initedMicroApps?.length) { // skip prerender if already rendered
    return
  }
  const actions = microApps.map(({ actions }: InitedMicroAppConfig) => ({ actions }) as InitedMicroAppConfig)
  let navigation = loadingLeftNavItems
  const { pathname } = window.location
  if (microApps.cached?.['/']) {
    const matched = Object.keys(microApps.cached).reduce((matched, path) => {
      if (pathname.startsWith(path) && path.length > matched.length) {
        return path
      }
      return matched
    }, '/')
    let icons = microApps.icons
    if (!icons || isEmptyObject(icons)) {
      const oneNav = (microApps.find(microApp => microApp.appName === SCM_ONE_NAV_APP_NAME)?.navigation || []) as NavItem[]
      icons = oneNav.reduce((map, item: NavItem) => {
        if (item.icon?.__approved_icon && !item.hidden) {
          if (item.key) {
            map[item.key] = item.icon
          }
          if (item.path) {
            map[item.path] = item.icon
          }
        }
        return map
      }, {} as Record<string, any>)
    }
    const addEarlyLoadingFlag = (item: NavItem) => {
      item._early_loading = true
      if (Array.isArray(item.children)) {
        item.children.forEach(addEarlyLoadingFlag)
      }
    }
    const cached = microApps.cached[matched]
    if (cached) {
      navigation = cached.map(item => {
        const icon = (item.key && icons[item.key]) || (item.path && icons[item.path]) || LeftNavLoadingIcon
        item.icon = icon
        addEarlyLoadingFlag(item)
        return item
      })
    }
  }
  setValuesInternal({
    microAppEagerLoadPromise: undefined,
    leftMainActions: Object.defineProperties(processActions(actions), {
      _action_early_init: { value: true },
      _nav_early_init: { value: navigation },
    }),
  })
}

export const microAppInitFailed = (err: Error) => {
  if (err.message === 'redirection') {
    return
  }
  clearLoadingMask()
  console.error('failed to init micro-apps', err)
}

const forceRerender = () => {
  forceRender()
}

export const useRerender = () => {
  useGlobalEventListener(RERENDER_EVENT, forceRerender)
}
